import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import api from '../../utils/api';
import { parseProductListResponse } from 'utils/parseListResponse';
import { infoAlertSx } from 'utils/designTokens';
import {
  getCategoryId,
  getProductId,
  getOfferType,
  lineIdsInclude,
  mergeTargetOptions
} from './offerUtils';

function parseCategoryListResponse(res) {
  const body = res?.data?.data ?? res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.categories)) return body.categories;
  if (Array.isArray(body?.category_list)) return body.category_list;
  return [];
}

export default function OfferTargetSelect({ form, setForm, seedOptions = [], title, requireStore = false }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const seedRef = useRef(seedOptions);
  seedRef.current = seedOptions;

  const isItem = form.offer_type === 'item';
  const getId = isItem ? getProductId : getCategoryId;
  const getLabel = (o) =>
    isItem ? o.name || o.name_en || '—' : o.name_en || o.name_ar || o.category_name_en || '—';

  const storeId = form.store_id ? String(form.store_id) : '';
  const seedKey = seedOptions.map((o) => getId(o)).join(',');

  useEffect(() => {
    if (!storeId || form.offer_type === 'discount') {
      setOptions(mergeTargetOptions(seedRef.current, [], getId));
      return;
    }

    setOptions(mergeTargetOptions(seedRef.current, [], getId));

    let cancelled = false;

    const fetchOptions = async () => {
      setLoading(true);
      try {
        let fetched = [];
        if (form.offer_type === 'category') {
          const r = await api.get('/product/category/list', { params: { store_id: storeId } });
          fetched = parseCategoryListResponse(r);
        } else if (form.offer_type === 'item') {
          const r = await api.get('/product/list', {
            params: { store_id: storeId, page: 1, limit: 500 }
          });
          fetched = parseProductListResponse(r).products;
        }
        if (!cancelled) {
          setOptions(mergeTargetOptions(seedRef.current, fetched, getId));
        }
      } catch (e) {
        console.error('Failed to load offer targets', e);
        if (!cancelled) {
          setOptions(mergeTargetOptions(seedRef.current, [], getId));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
  }, [storeId, form.offer_type, seedKey, isItem]);

  const selected = options.filter((o) => lineIdsInclude(form.line_ids, getId(o)));
  const showTargetSpinner = loading && options.length === 0;
  const heading =
    title || `${getOfferType(form.offer_type).label} — ${requireStore ? 'select targets' : 'change selection'}`;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {heading}
      </Typography>
      {requireStore && !form.store_id ? (
        <Alert severity="info" sx={infoAlertSx}>
          Select a store in the previous step first.
        </Alert>
      ) : showTargetSpinner ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={getLabel}
          isOptionEqualToValue={(a, b) => String(getId(a)) === String(getId(b))}
          value={selected}
          onChange={(_, v) => setForm((p) => ({ ...p, line_ids: v.map(getId).filter(Boolean) }))}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label={isItem ? 'Products' : 'Categories'}
              placeholder={isItem ? 'Search products…' : 'Search categories…'}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={getLabel(option)} {...getTagProps({ index })} size="small" key={getId(option)} />
            ))
          }
        />
      )}
    </Box>
  );
}

OfferTargetSelect.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  seedOptions: PropTypes.array,
  title: PropTypes.string,
  requireStore: PropTypes.bool
};
